import { useTranslation } from "react-i18next";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import {
  GitFork,
  CheckCircle,
  XCircle,
  UserIcon,
  Hourglass,
  MapPin,
  CalendarCheck,
  MessageSquare,
  Building2,
  Users,
  MinusCircle,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  STATUS_LABEL,
  STATUS_STYLES,
  NODE_HEIGHT,
  type IWorkflowStep,
  type StepStatus,
} from "./types";
import { formatDate } from "@/utils/helper";

type StepNodeData = IWorkflowStep & Record<string, unknown>;

// Per-person decision indicator inside a parallel-approval gate.
const personStatusMeta = (s?: string) => {
  const v = (s ?? "").toLowerCase();
  if (v === "approved") return { Icon: CheckCircle, color: "text-green-500" };
  if (v === "rejected") return { Icon: XCircle, color: "text-red-500" };
  // Gate completed without this member acting (or never reached).
  if (v === "skipped" || v === "not_started")
    return { Icon: MinusCircle, color: "text-gray-300" };
  return { Icon: Hourglass, color: "text-orange-500" };
};

const StepNode = ({ data }: NodeProps) => {
  const { t } = useTranslation(["common", "my-space"]);
  const d = data as StepNodeData;
  const status: StepStatus = d.status ?? "pending";
  const styles = STATUS_STYLES[status];

  const StatusIcon =
    status === "approved"
      ? CheckCircle
      : status === "rejected"
        ? XCircle
        : Hourglass;

  const approvers = d.approvers ?? [];
  const isParallel = d.type === "parallel_approval" && approvers.length > 0;

  return (
    <div
      className={`border rounded-xl w-[372px] bg-white shadow-xs overflow-hidden flex flex-col ${styles.border} ${styles.disabled ? "opacity-60" : ""}`}
      style={{ height: NODE_HEIGHT }}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 py-2 shrink-0 ${styles.headerBg}`}
      >
        <span
          className={`text-xs font-semibold uppercase tracking-wide ${styles.headerText}`}
        >
          {t("common:workflow.step_number", {
            number: String(d.stepOrder).padStart(2, "0"),
          })}
          {status === "current"
            ? ` ${t("common:workflow.current_suffix")}`
            : ""}
        </span>
        <span
          className={`flex items-center gap-1 text-xs font-medium ${styles.headerText}`}
        >
          <StatusIcon className="size-3" />
          {t(STATUS_LABEL[status])}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-2.5 px-4 py-3 flex-1 min-h-0 overflow-hidden">
        {/* Node name */}
        <div className="flex items-center gap-2 shrink-0">
          <div
            className={`size-7 rounded flex items-center justify-center shrink-0 ${styles.iconBg}`}
          >
            {isParallel ? (
              <Users className={`size-3.5 ${styles.iconColor}`} />
            ) : (
              <GitFork className={`size-3.5 ${styles.iconColor}`} />
            )}
          </div>
          <span
            className={`text-sm font-bold truncate ${styles.disabled ? "text-gray-400" : "text-gray-900"}`}
          >
            {d.nodeName}
          </span>
          {isParallel && (
            <span className="ms-auto shrink-0 text-[10px] font-medium text-gray-400 bg-gray-100 border border-border px-1.5 py-px rounded-full leading-none">
              {t("common:workflow.n_approvers", { count: approvers.length })}
            </span>
          )}
        </div>

        {/* Approvers — parallel gate: everyone on the gate + their decision */}
        {isParallel ? (
          <div className="flex flex-col gap-1.5 shrink-0 max-h-[132px] overflow-y-auto pe-0.5">
            {approvers.map((a, i) => {
              const pm = personStatusMeta(a.status);
              return (
                <div
                  key={i}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-100"
                >
                  <Avatar
                    className={`size-7 shrink-0 ${styles.disabled ? "opacity-50" : "bg-primary/10"}`}
                  >
                    <AvatarImage src={a.profileImage} alt={a.firstName} />
                    <AvatarFallback>
                      <UserIcon size={11} className="text-white" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-semibold truncate block text-gray-900">
                      {a.firstName} {a.lastName}
                    </span>
                    {a.role && (
                      <span className="text-[10px] text-gray-400 truncate block capitalize">
                        {a.role.replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                  <pm.Icon className={`size-3.5 shrink-0 ${pm.color}`} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-100 shrink-0">
            {d.approver ? (
              <>
                <Avatar
                  className={`size-9 shrink-0 ${styles.disabled ? "opacity-50" : "bg-primary/10"}`}
                >
                  <AvatarImage
                    src={d.approver.profileImage}
                    alt={d.approver.firstName}
                  />
                  <AvatarFallback>
                    <UserIcon size={13} className="text-white" />
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span
                      className={`text-xs font-semibold truncate ${styles.disabled ? "text-gray-400" : "text-gray-900"}`}
                    >
                      {d.approver.firstName} {d.approver.lastName}
                    </span>
                    {d.approver.employeeId && (
                      <span className="text-[10px] text-gray-400 bg-white border border-border px-1.5 py-px rounded-full shrink-0 font-medium leading-none">
                        {d.approver.employeeId}
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-[11px] mt-0.5 block truncate ${styles.disabled ? "text-gray-300" : "text-gray-500"}`}
                  >
                    {d.positionName}
                  </span>
                  {d.approver.department && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <Building2
                        className={`size-2.5 shrink-0 ${styles.disabled ? "text-gray-300" : "text-gray-400"}`}
                      />
                      <span
                        className={`text-[10px] truncate ${styles.disabled ? "text-gray-300" : "text-gray-400"}`}
                      >
                        {d.approver.department}
                      </span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2.5">
                <div className="size-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <UserIcon size={14} className="text-gray-400" />
                </div>
                <span className="text-xs text-gray-400">
                  {t("common:workflow.not_assigned")}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Meta rows */}
        <div className="flex flex-col gap-1.5 flex-1 min-h-0 overflow-hidden">
          {/* Branch */}
          {d.branch && (
            <div className="flex items-center gap-1.5 shrink-0">
              <MapPin
                className={`size-3 shrink-0 ${styles.disabled ? "text-gray-300" : "text-gray-400"}`}
              />
              <span
                className={`text-[11px] truncate ${styles.disabled ? "text-gray-300" : "text-gray-500"}`}
              >
                {d.branch as string}
              </span>
            </div>
          )}

          {/* Awaiting approval — current step only */}
          {status === "current" && (
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="relative flex size-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                <span className="relative inline-flex size-2 rounded-full bg-orange-500" />
              </span>
              <span className="text-[11px] font-semibold text-orange-600">
                {t("common:workflow.awaiting_approval")}
              </span>
            </div>
          )}

          {/* Approved / Rejected timestamp */}
          {(status === "approved" || status === "rejected") && d.actionAt && (
            <div className="flex items-center gap-1.5 shrink-0">
              <CalendarCheck
                className={`size-3 shrink-0 ${status === "rejected" ? "text-red-400" : "text-green-500"}`}
              />
              <span
                className={`text-[11px] font-medium ${status === "rejected" ? "text-red-600" : "text-green-600"}`}
              >
                {t("common:workflow.status_on_date", {
                  status:
                    status === "rejected"
                      ? t("common:status.rejected")
                      : t("common:status.approved"),
                  date: formatDate(d.actionAt as string),
                })}
              </span>
            </div>
          )}

          {/* Comments */}
          {d.comments &&
            (status === "rejected" ? (
              <div className="flex items-start gap-1.5 shrink-0 bg-red-50 border border-red-100 rounded-md px-2.5 py-1.5">
                <MessageSquare className="size-3 shrink-0 mt-px text-red-400" />
                <span className="text-[11px] line-clamp-2 text-red-600 font-medium leading-relaxed">
                  {d.comments as string}
                </span>
              </div>
            ) : (
              <div className="flex items-start gap-1.5 shrink-0">
                <MessageSquare
                  className={`size-3 shrink-0 mt-px ${styles.disabled ? "text-gray-300" : "text-gray-400"}`}
                />
                <span
                  className={`text-[11px] line-clamp-2 ${styles.disabled ? "text-gray-300" : "text-gray-500"}`}
                >
                  {d.comments as string}
                </span>
              </div>
            ))}
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Top}
        className="bg-white! border-2! border-primary! w-3! h-3!"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="bg-white! border-2! border-primary! w-3! h-3!"
        isConnectable={false}
      />
    </div>
  );
};

export default StepNode;
